import React from "react";
import DropdownMenu from "../../DropdownMenu";

/**
 * Creates props for chair time utilization graph to be used with BlankCard component
 * @param {Array} data - Array of daily data objects containing chair time utilization information
 * @param {Object} options - Additional options like height, chartHeight, goal, etc.
 * @returns {Object} - Props object ready to spread into BlankCard component
 */
export const createChairTimeUtilization = (data, options = {}, viewState, setViewState) => {
  // Set default values for options
  const {
    height = 300,
    chartHeight = 250,
    goal = 90, // Default goal percentage if not provided
  } = options;

  const viewOptions = [
    { id: "day", name: "Day" },
    { id: "month", name: "Month" },
    { id: "year", name: "Year" }
  ];

  // Filter out invalid data
  const validData = data.filter(item => 
    item && typeof item.chairTimeUtilization !== 'undefined' && item.chairTimeUtilization !== null
  );
  
  // Calculate average utilization across all days
  const averageUtilization = validData.length > 0
    ? (validData.reduce((sum, item) => sum + (item.chairTimeUtilization || 0), 0) / validData.length).toFixed(1)
    : 0;
  
  // Maximum wait time
  const maxUtilization = validData.length > 0
    ? Math.max(...validData.map(day => Number(day.chairTimeUtilization || 0))).toFixed(0)
    : 0;
  
  // Min wait time
  const minUtilization = validData.length > 0
    ? Math.min(...validData.map(day => Number(day.chairTimeUtilization || 0))).toFixed(0)
    : 0;

  let seriesData = [];
  let categories = [];

  // Process data for monthly view
  const month_to_values = {};
  // Process data for yearly view
  const year_to_values = {};

  // Prepare aggregated data for both monthly and yearly views
  validData.forEach(item => {
    if (!item.date) return;
    
    // For monthly aggregation
    const dateParts = item.date.split('-');
    if (dateParts.length >= 2) {
      const monthYear = `${dateParts[0]}-${dateParts[1]}`; // YYYY-MM
      
      if (!month_to_values[monthYear]) {
        month_to_values[monthYear] = [];
      }
      
      month_to_values[monthYear].push(item.chairTimeUtilization || 0);
    }
    
    // For yearly aggregation
    const year = dateParts[0]; // YYYY
    if (year) {
      if (!year_to_values[year]) {
        year_to_values[year] = [];
      }
      
      year_to_values[year].push(item.chairTimeUtilization || 0);
    }
  });

  // Function to format month labels
  const formatMonthLabel = (monthYear) => {
    if (!monthYear || monthYear.split('-').length < 2) return monthYear;
    
    const [year, month] = monthYear.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month, 10) - 1;
    
    return `${monthNames[monthIndex]} ${year}`;
  };


  // Prepare data for the line chart based on viewState
  if (viewState === "day") {
    seriesData = validData.map(item => item.chairTimeUtilization || 0);
    categories = validData.map(item => item.date);
  } else if (viewState === "month") {
    // Monthly view - use aggregated data
    const sortedMonths = Object.keys(month_to_values).sort();
    
    seriesData = sortedMonths.map(month => {
      const values = month_to_values[month];
      return values.length > 0 ? 
        values.reduce((sum, val) => sum + val, 0) / values.length : 0;
    });
    
    categories = sortedMonths.map(month => formatMonthLabel(month));
  } else if (viewState === "year") {
    // Yearly view - use aggregated data
    const sortedYears = Object.keys(year_to_values).sort();
    
    seriesData = sortedYears.map(year => {
      const values = year_to_values[year];
      return values.length > 0 ? 
        values.reduce((sum, val) => sum + val, 0) / values.length : 0;
    });
    
    categories = sortedYears;
  }
  
  // Get status based on average utilization vs goal
  const getStatus = () => {
    if (averageUtilization >= goal) {
      return { 
        status: "great", 
        badge: "Exceeding Goal" 
      }
    }
    if (averageUtilization >= goal * 0.8) {
      return { 
        status: "good", 
        badge: "Almost There" 
      }
    }
    return { 
      status: "critical", 
      badge: "Needs Attention" 
    }
  }

  // Calculate stats for the card
  const stats = {
    "Maximum Chair Time Utilization": `${maxUtilization}%`,
    "Minimum Chair Time Utilization": `${minUtilization}%`
  };
  
  // Custom header component for BlankCard
  const customHeader = (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 mb-1">
        <span className="text-3xl font-bold leading-tight">{averageUtilization}%</span>
        {/* <div className="flex items-center gap-1">
          <div 
            className={`flex items-center gap-1 ${Number(trendPercentage) >= 0 ? 'text-green-500' : 'text-red-500'}`}
            title={`Trend: ${Number(trendPercentage) >= 0 ? '+' : ''}${trendPercentage}% compared to start of range`}
          >
            {Number(trendPercentage) >= 0 
              ? <FiTrendingUp className="w-4 h-4" /> 
              : <FiTrendingDown className="w-4 h-4" />
            }
            <span className="text-sm font-medium">
              {Number(trendPercentage) >= 0 ? '+' : ''}
              {trendPercentage}%
            </span>
          </div>
          <div 
            className="text-gray-400 hover:text-gray-600 cursor-help relative group"
          >
            <FiInfo className="w-3.5 h-3.5" />
            <div className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 px-4 py-4 bg-[#6955EA] text-white text-sm rounded-lg shadow-lg w-64 pointer-events-none">
              Trend is calculated as the percentage difference between the first and last point in the selected time range
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div> */}
      </div>
      <span className="text-md text-left text-gray-500">Avg. Chair Time Utilization</span>
    </div>
  );

  // Create a dropdown wrapper component to force re-render when grouping changes
  const GroupingDropdownWrapper = () => {
    return (
      <DropdownMenu
        buttonText={viewOptions.find(opt => opt.id === viewState)?.name}
        options={viewOptions}
        selectedOption={viewState}
        onSelect={setViewState}
        buttonClassName="inline-flex justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
      />
    );
  };

  // Custom footer with dropdown menu
  const customFooter = (
    <div className="mt-4 flex justify-left items-center pl-4 pb-2">
      <div className="w-auto">
        <label className="text-md text-gray-500 mb-1 inline-block">View by: &nbsp;</label>
        <GroupingDropdownWrapper />
      </div>
    </div>
  );
  
  // Return props for BlankCard
  const statusInfo = getStatus()
  return {
    title: "Chair Time Utilization", // This will be replaced by the custom header
    description: "",  // No description needed
    chartType: "line",
    chartData: {
      series: seriesData,
      categories: categories
    },
    minValue: Math.min(...seriesData),
    maxValue: Math.max(...seriesData),
    chartHeight,
    height,
    status: statusInfo.status,
    badge: statusInfo.badge,
    stats,
    customHeader, // Add custom header component
    customFooter, // Add custom footer with view state dropdown
    goal: goal, // Goal percentage
    isHigherBetter: true, // Higher values are better for utilization
    options: {
      yAxisFormatter: value => Math.round(value),
      tooltipFormatter: value => `${value.toFixed(1)}%`,
      tooltipText: "Chair Time Utilization"
    },
    smallStats: true
  };
};

export default createChairTimeUtilization; 